import { useState, useEffect } from "react";
import { isNativeCapacitorApp } from "@/lib/runtime";

interface PlatformInfo {
  isNativeIOS: boolean;
  isNativeAndroid: boolean;
  isNative: boolean;
  isWeb: boolean;
  isPWA: boolean;
}

export const usePlatform = (): PlatformInfo => {
  const [platform, setPlatform] = useState<PlatformInfo>(() => detect());
  
  useEffect(() => {
    setPlatform(detect());
  }, []);

  return platform;
};

type NativePlatform = "ios" | "android" | "web";

function inferNativePlatform(): NativePlatform {
  if (typeof window === "undefined") return "web";

  const capacitor = (window as any).Capacitor;
  const reportedPlatform = capacitor?.getPlatform?.();

  if (reportedPlatform === "ios" || reportedPlatform === "android" || reportedPlatform === "web") {
    return reportedPlatform;
  }

  const runningNatively = isNativeCapacitorApp() || window.location.protocol === "capacitor:";

  if (!runningNatively) {
    return "web";
  }

  const userAgent = window.navigator?.userAgent ?? "";

  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";

  return "ios";
}

function detect(): PlatformInfo {
  if (typeof window === "undefined") {
    return {
      isNativeIOS: false,
      isNativeAndroid: false,
      isNative: false,
      isWeb: true,
      isPWA: false,
    };
  }

  const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const nativePlatform = inferNativePlatform();
  const isNative = nativePlatform !== "web";

  return {
    isNativeIOS: nativePlatform === "ios",
    isNativeAndroid: nativePlatform === "android",
    isNative,
    isWeb: !isNative,
    isPWA: isStandalone && !isNative,
  };
}

/** Imperative check (no hook needed) */
export function isNativeIOS(): boolean {
  return inferNativePlatform() === "ios";
}
